// components/Pages/Register/RegisterStage2.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { API_BASE } from "../../../ApiUrl";
import Sidebar from "../../Candidate/Layout/Sidebar";
import Header from "../../Candidate/Layout/Header";

// Import tab components
import Tab1PersonalProfile from "./Tabs/Tab1PersonalProfile";
import Tab2Qualifications from "./Tabs/Tab2Qualifications";
import Tab3Experience from "./Tabs/Tab3Experience";
import Tab4Competencies from "./Tabs/Tab4Competencies";
import Tab5References from "./Tabs/Tab5References";
import Tab6Deployment from "./Tabs/Tab6Deployment";

// Import hooks
import { useProfessionalProfile } from "./hooks/useProfessionalProfile";
import { useCredentialRecords } from "./hooks/useCredentialRecords";
import { useExperienceRecords } from "./hooks/useExperienceRecords";
import { useCapabilityRecords } from "./hooks/useCapabilityRecords";
import { useReferenceRecords } from "./hooks/useReferenceRecords";
import { useDeploymentRecords } from "./hooks/useDeploymentRecords";
import { useClientApprovalRecords } from "./hooks/useClientApprovalRecords";

// Import constants (for reference values)
import { SCOPE_OPTIONS } from "./constants";

import "./RegisterStage2.css";

const RegisterStage2 = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState(1);
  const [professionalProfileId, setProfessionalProfileId] = useState(null);
  const [tenantId, setTenantId] = useState(null);
  const [relatedData, setRelatedData] = useState(null);

  // ----- Reference values (Role, Availability, etc.) -----
  const [referenceValues, setReferenceValues] = useState([]);
  const [referenceValuesLoading, setReferenceValuesLoading] = useState(false);

  useEffect(() => {
    const fetchReferenceValues = async () => {
      setReferenceValuesLoading(true);
      try {
        const res = await axios.get(`${API_BASE}/reference-values/`);
        setReferenceValues(res?.data?.data || []);
      } catch (err) {
        console.error("Failed to load reference values", err);
      } finally {
        setReferenceValuesLoading(false);
      }
    };
    fetchReferenceValues();
  }, []);

  const referenceValuesByType = useMemo(() => {
    const groups = {};
    referenceValues.forEach((rv) => {
      const type = rv?.option_set_value?.option_type;
      if (!type || rv.is_active === false) return;
      if (!groups[type]) groups[type] = [];
      groups[type].push(rv);
    });
    Object.values(groups).forEach((list) =>
      list.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    );
    return groups;
  }, [referenceValues]);

  const roleOptions = referenceValuesByType["Role Category"] || [];
  const availabilityStatusOptions =
    referenceValuesByType["Availability Status"] || [];
  const rateTypeOptions = referenceValuesByType["Rate Type"] || [];
  const engagementTypeOptions = referenceValuesByType["Engagement Type"] || [];
  const genderOptions = referenceValuesByType["Gender"] || [];

  // ----- Hooks for each tab -----
  const professionalProfile = useProfessionalProfile(professionalProfileId);
  const credentialRecords = useCredentialRecords(
    professionalProfileId,
    tenantId,
  );
  const experienceRecords = useExperienceRecords(
    professionalProfileId,
    tenantId,
  );
  const capabilityRecords = useCapabilityRecords(professionalProfileId);
  const referenceRecords = useReferenceRecords(professionalProfileId, tenantId);
  const deploymentRecords = useDeploymentRecords(
    professionalProfileId,
    tenantId,
  );
  const clientApproval = useClientApprovalRecords(
    professionalProfileId,
    tenantId,
  );

  const submitDeploymentOnly = async () => {
    setLoading(true);
    try {
      await deploymentRecords.submitDeploymentOnly();
      Swal.fire({
        icon: "success",
        title: "Deployment details saved!",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      const message =
        err?.response?.data?.message || err.message || "Save failed.";
      Swal.fire({ icon: "error", title: "Save Failed", text: message });
    } finally {
      setLoading(false);
    }
  };

  // ----- Load related records from the unified API -----
  const loadRelatedRecords = async (profileId) => {
    if (!profileId) return;
    try {
      const res = await axios.get(
        `${API_BASE}/professional-profiles/${profileId}/related-records/`,
      );
      const data = res?.data?.data || res?.data;
      setRelatedData(data);

      // Hydrate each hook with the related data
      // Professional Profile - if data has profile info
      if (data.profile) {
        professionalProfile.hydrateFromRelatedRecords(data);
      } else {
        // Fallback: try to hydrate from the data directly
        professionalProfile.hydrateFromRelatedRecords(data);
      }

      // Credentials
      if (data.credentials) {
        credentialRecords.hydrateFromRelatedRecords(data);
      }

      // Experience - Handle both employment_records and project_records
      if (data.employment_records || data.project_records) {
        // If data has employment_records or project_records at root level
        if (typeof experienceRecords.hydrateFromRelatedRecords === "function") {
          experienceRecords.hydrateFromRelatedRecords(data);
        }
      } else if (data.experience) {
        // If data has experience wrapper
        if (typeof experienceRecords.hydrateFromRelatedRecords === "function") {
          experienceRecords.hydrateFromRelatedRecords(data);
        }
      }

      // Capabilities
      if (data.capabilities) {
        if (typeof capabilityRecords.hydrateFromRelatedRecords === "function") {
          capabilityRecords.hydrateFromRelatedRecords(data);
        } else if (
          typeof capabilityRecords.hydrateFromCapabilities === "function"
        ) {
          capabilityRecords.hydrateFromCapabilities(data.capabilities);
        }
      }

      // References - Updated to use hydrateFromRelatedRecords
      if (data.references || data.credentials) {
        if (typeof referenceRecords.hydrateFromRelatedRecords === "function") {
          referenceRecords.hydrateFromRelatedRecords(data);
        }
      }

      // Deployment
      if (
        data.contacts ||
        data.credentials ||
        data.ppe_sizes ||
        data.consent_records
      ) {
        if (typeof deploymentRecords.hydrateFromRelatedRecords === "function") {
          deploymentRecords.hydrateFromRelatedRecords(data);
        }
      }

      // Client approvals
      if (data.client_approvals) {
        if (typeof clientApproval.hydrateFromRelatedRecords === "function") {
          clientApproval.hydrateFromRelatedRecords(data);
        }
      }
    } catch (err) {
      console.error("Failed to load related records", err);
      // If the related-records endpoint fails, fall back to individual endpoints
      // This ensures backward compatibility
      await loadIndividualRecords(profileId);
    }
  };

  // ----- Fallback: Load individual records (existing behavior) -----
  const loadIndividualRecords = async (profileId) => {
    try {
      // Load professional profile
      const res = await axios.get(`${API_BASE}/users/${id}/`);
      const userData = res?.data?.data;
      const profile = userData?.professional_profile;
      if (profile?.id) {
        professionalProfile.hydrateFromProfile(profile);
      }
    } catch (err) {
      console.error("Failed to load individual records", err);
    }
  };

  // ----- Load existing profile and set professionalProfileId -----
  useEffect(() => {
    const loadExistingProfile = async () => {
      const routeId =
        id || window.location.pathname.split("/").filter(Boolean).pop();
      if (!routeId || routeId === "register-stage2") return;

      try {
        const res = await axios.get(`${API_BASE}/users/${routeId}/`);
        const userData = res?.data?.data;
        const profile = userData?.professional_profile;
        if (profile?.id) {
          setProfessionalProfileId(profile.id);
          setTenantId(userData.tenant);
          // Load related records using the unified API
          await loadRelatedRecords(profile.id);
        }
      } catch (err) {
        console.error("Failed to load existing profile", err);
      }
    };

    loadExistingProfile();
  }, [id]);

  // ----- Tab validation wrappers -----
  const validateTab1 = () => {
    const errs = professionalProfile.validate();
    return errs;
  };

  const validateTab2 = () => credentialRecords.validate();
  const validateTab3 = () => experienceRecords.validate();
  const validateTab4 = () => capabilityRecords.validate();
  const validateTab5 = () => referenceRecords.validate();
  const validateTab6 = () => deploymentRecords.validate();

  // ----- Per-tab submit handlers -----
// ----- Per-tab submit handlers -----
const submitTab = async (tabNumber) => {
  let errors = {};
  let submitFn = null;
  let successMessage = "This section has been saved successfully.";

  switch (tabNumber) {
    case 1:
      errors = validateTab1();
      submitFn = professionalProfile.submit;
      break;
    case 2:
      errors = validateTab2();
      submitFn = credentialRecords.submit;
      break;
    case 3:
      // For Tab 3, we only want to submit client approvals
      errors = validateTab3();
      submitFn = async () => {
        // Only submit client approvals via credential-records API
        await clientApproval.submitClientApprovals();
      };
      successMessage = "Client approvals saved successfully.";
      break;
    case 4:
      errors = validateTab4();
      submitFn = capabilityRecords.submit;
      break;
    case 5:
      errors = validateTab5();
      submitFn = async () => {
        await referenceRecords.submitMembershipsPublicationsAwards();
        await referenceRecords.submitReferences();
      };
      break;
    case 6:
      errors = validateTab6();
      return;
    default:
      return;
  }

  if (Object.keys(errors).length > 0) {
    Swal.fire({
      icon: "warning",
      title: "Validation Error",
      text: "Please fill in all required fields.",
      footer: Object.values(errors).join("\n"),
    });
    return;
  }

  setLoading(true);
  setError("");
  try {
    await submitFn();
    const result = await Swal.fire({
      icon: "success",
      title: "Saved!",
      text: successMessage,
      showCancelButton: true,
      confirmButtonText: "Continue to next tab",
      cancelButtonText: "Stay on this tab",
      reverseButtons: true,
    });
    if (result.isConfirmed) {
      setActiveTab(tabNumber + 1);
    }
  } catch (err) {
    const message =
      err?.response?.data?.message || err.message || "Save failed.";
    setError(message);
    Swal.fire({ icon: "error", title: "Save Failed", text: message });
  } finally {
    setLoading(false);
  }
};

  // For Tab 3's independent submits (employment and projects separately)
  const submitEmploymentOnly = async () => {
    const errors = validateTab3();
    if (Object.keys(errors).length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: Object.values(errors).join("\n"),
      });
      return;
    }
    setLoading(true);
    try {
      await experienceRecords.submitEmployment();
      Swal.fire({
        icon: "success",
        title: "Employment records saved!",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Save Failed", text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const submitProjectsOnly = async () => {
    const errors = validateTab3();
    if (Object.keys(errors).length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: Object.values(errors).join("\n"),
      });
      return;
    }
    setLoading(true);
    try {
      await experienceRecords.submitProjects();
      Swal.fire({
        icon: "success",
        title: "Project records saved!",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Save Failed", text: err.message });
    } finally {
      setLoading(false);
    }
  };

  // For Tab 5's independent MPA submit
  const submitMPAOnly = async () => {
    const errors = validateTab5();
    if (Object.keys(errors).length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: Object.values(errors).join("\n"),
      });
      return;
    }
    setLoading(true);
    try {
      await referenceRecords.submitMembershipsPublicationsAwards();
      Swal.fire({
        icon: "success",
        title: "Saved!",
        text: "Memberships, publications and awards saved.",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Save Failed", text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const submitDeclarationOnly = async () => {
    const tenantUser = JSON.parse(localStorage.getItem("tenant_user") || "{}");
    const currentUserId = tenantUser?.user_id;
    const currentTenantId = tenantUser?.tenant_id;

    if (!currentUserId || !currentTenantId || !professionalProfileId) {
      Swal.fire({
        icon: "error",
        title: "Missing profile data",
        text: "User, tenant, or professional profile could not be determined. Please reload the page.",
      });
      return;
    }

    setLoading(true);
    try {
      const { accuracy, conflict_of_interest } =
        deploymentRecords.declarationData;
      const now = new Date().toISOString();

      const payload = [
        {
          tenant: currentTenantId,
          user: currentUserId,
          professional: professionalProfileId,
          document_version: "V1",
          consent_type: "PROFILE_ACCURACY",
          is_granted: !!accuracy,
          granted_at: accuracy ? now : null,
        },
        {
          tenant: currentTenantId,
          user: currentUserId,
          professional: professionalProfileId,
          document_version: "V1",
          consent_type: "CONFLICT_OF_INTEREST",
          is_granted: !!conflict_of_interest,
          granted_at: conflict_of_interest ? now : null,
        },
      ];

      await axios.post(`${API_BASE}/consent-records/`, payload);

      Swal.fire({
        icon: "success",
        title: "Declarations saved!",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      const message =
        err?.response?.data?.message || err.message || "Save failed.";
      Swal.fire({ icon: "error", title: "Save Failed", text: message });
    } finally {
      setLoading(false);
    }
  };

  const [stage2Status, setStage2Status] = useState(null);

  // Fetch stage2 status
  const fetchStage2Status = async (professionalId) => {
    try {
      const res = await axios.get(
        `${API_BASE}/professional-profiles/${professionalId}/stage2_status/`,
      );
      if (res.data.success) {
        setStage2Status(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching stage2 status:", err);
    }
  };

  // Fetch status when component mounts or professionalProfileId changes
  useEffect(() => {
    if (professionalProfileId) {
      fetchStage2Status(professionalProfileId);
    }
  }, [professionalProfileId]);

  // ----- Final Submission (Tab 6) -----
  const handleSubmit = async () => {
    const errors = {
      ...validateTab1(),
      ...validateTab2(),
      ...validateTab3(),
      ...validateTab4(),
      ...validateTab5(),
      ...validateTab6(),
    };

    if (Object.keys(errors).length > 0) {
      const firstInvalidTab =
        [
          validateTab1,
          validateTab2,
          validateTab3,
          validateTab4,
          validateTab5,
          validateTab6,
        ].findIndex((fn) => Object.keys(fn()).length > 0) + 1;
      setActiveTab(firstInvalidTab);
      Swal.fire({
        icon: "warning",
        title: "Please fix errors",
        text: Object.values(errors).join("\n"),
      });
      return;
    }

    if (!professionalProfileId) {
      Swal.fire({
        icon: "error",
        title: "Missing profile",
        text: "Your professional profile could not be found. Please complete Tab 1 first.",
      });
      setActiveTab(1);
      return;
    }

    setLoading(true);
    setError("");

    const payload = {
      personal: professionalProfile.personalData,
      professional: professionalProfile.professionalData,
      availability: professionalProfile.availabilityData,
      commercial: professionalProfile.commercialData,
      travel_documents: credentialRecords.travelDocuments,
      marine_documents: credentialRecords.marineDocuments,
      education: credentialRecords.educationRecords,
      certifications: credentialRecords.certificationRecords,
      training: credentialRecords.trainingRecords,
      employment: experienceRecords.employmentRecords,
      projects: experienceRecords.projectRecords,
      languages: capabilityRecords.languageRecords,
      standards: capabilityRecords.standardRecords,
      equipment: capabilityRecords.equipmentRecords,
      software: capabilityRecords.softwareRecords,
      memberships: referenceRecords.membershipRecords,
      publications: referenceRecords.publicationRecords,
      awards: referenceRecords.awardRecords,
      references: referenceRecords.referenceRecords,
      emergency_contacts: deploymentRecords.emergencyContactRecords,
      medical_fitness: deploymentRecords.medicalFitnessRecords,
      ppe_sizes: deploymentRecords.ppeSizes,
      declaration: deploymentRecords.declarationData,
    };

    try {
      let response;
      const status = stage2Status?.profile_status;

      // Determine which endpoint to use based on stage2 status
      if (["STAGE2_SUBMITTED", "RETURNED"].includes(status)) {
        // Resubmit for existing submissions
        response = await axios.post(
          `${API_BASE}/professional-profiles/${professionalProfileId}/stage2_resubmit/`,
          payload,
        );
      } else if (status === "STAGE1_COMPLETE" || !status) {
        // New submission for first time
        response = await axios.post(
          `${API_BASE}/professional-profiles/${professionalProfileId}/stage2_submit/`,
          payload,
        );
      } else {
        // Default fallback - use submit
        response = await axios.post(
          `${API_BASE}/professional-profiles/${professionalProfileId}/stage2_submit/`,
          payload,
        );
      }

      // Success message based on response
      const isResubmit = ["STAGE2_SUBMITTED", "RETURNED"].includes(status);
      const successMessage = isResubmit
        ? "Your professional profile has been updated and resubmitted successfully!"
        : "Your professional profile has been submitted successfully!";

      await Swal.fire({
        icon: "success",
        title: isResubmit
          ? "Profile Updated & Resubmitted!"
          : "Professional Profile Created!",
        text: successMessage,
        timer: 2500,
        showConfirmButton: true,
      });

      navigate("/profile");
    } catch (err) {
      const message =
        err?.response?.data?.message || err.message || "Submission failed.";
      setError(message);
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: message,
      });
    } finally {
      setLoading(false);
    }
  };

  // Navigation
  const goToPrevTab = (fromTab) => setActiveTab(fromTab - 1);

  // ----- Render -----
  const tabClass = (tabNum) => {
    if (tabNum === activeTab)
      return "register-stage2-tab register-stage2-tab--active";
    if (tabNum < activeTab)
      return "register-stage2-tab register-stage2-tab--done";
    return "register-stage2-tab";
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />
        <div className="register-stage2-page">
          <div className="register-stage2-wrapper">
            {error && (
              <div
                className="register-stage2-alert register-stage2-alert--error"
                role="alert"
              >
                <span className="register-stage2-alert__message">
                  <strong>Error:</strong> {error}
                </span>
                <button
                  type="button"
                  className="register-stage2-alert__close-btn"
                  onClick={() => setError("")}
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
            )}

            <div className="register-stage2-body">
              {/* Tab Navigation */}
              <div className="register-stage2-tabs">
                <div className={tabClass(1)} onClick={() => setActiveTab(1)}>
                  <span className="register-stage2-tab__number">1</span>
                  <div className="register-stage2-tab__text">
                    <span className="register-stage2-tab__label">
                      Personal Profile
                    </span>
                  </div>
                </div>
                <div className={tabClass(2)} onClick={() => setActiveTab(2)}>
                  <span className="register-stage2-tab__number">2</span>
                  <div className="register-stage2-tab__text">
                    <span className="register-stage2-tab__label">
                      Qualifications & Credentials
                    </span>
                  </div>
                </div>
                <div className={tabClass(3)} onClick={() => setActiveTab(3)}>
                  <span className="register-stage2-tab__number">3</span>
                  <div className="register-stage2-tab__text">
                    <span className="register-stage2-tab__label">
                      Experience
                    </span>
                  </div>
                </div>
                <div className={tabClass(4)} onClick={() => setActiveTab(4)}>
                  <span className="register-stage2-tab__number">4</span>
                  <div className="register-stage2-tab__text">
                    <span className="register-stage2-tab__label">
                      Competencies
                    </span>
                  </div>
                </div>
                <div className={tabClass(5)} onClick={() => setActiveTab(5)}>
                  <span className="register-stage2-tab__number">5</span>
                  <div className="register-stage2-tab__text">
                    <span className="register-stage2-tab__label">
                      Professional References
                    </span>
                  </div>
                </div>
                <div className={tabClass(6)} onClick={() => setActiveTab(6)}>
                  <span className="register-stage2-tab__number">6</span>
                  <div className="register-stage2-tab__text">
                    <span className="register-stage2-tab__label">
                      Deployment & Compliance
                    </span>
                  </div>
                </div>
              </div>

              {/* Tab Content */}
              <div className="register-stage2-content">
                <div className="register-stage2-form">
                  {activeTab === 1 && (
                    <Tab1PersonalProfile
                      personalData={professionalProfile.personalData}
                      setPersonalData={professionalProfile.setPersonalData}
                      professionalData={professionalProfile.professionalData}
                      setProfessionalData={
                        professionalProfile.setProfessionalData
                      }
                      availabilityData={professionalProfile.availabilityData}
                      setAvailabilityData={
                        professionalProfile.setAvailabilityData
                      }
                      commercialData={professionalProfile.commercialData}
                      setCommercialData={professionalProfile.setCommercialData}
                      errors={{}}
                      loading={loading}
                      handlePersonalChange={
                        professionalProfile.handlePersonalChange
                      }
                      handleNationalitiesChange={
                        professionalProfile.handleNationalitiesChange
                      }
                      handleProfessionalChange={
                        professionalProfile.handleProfessionalChange
                      }
                      handleMultiSelectChange={
                        professionalProfile.handleMultiSelectChange
                      }
                      handleAvailabilityChange={
                        professionalProfile.handleAvailabilityChange
                      }
                      handleEngagementTypesChange={
                        professionalProfile.handleEngagementTypesChange
                      }
                      handleCommercialChange={
                        professionalProfile.handleCommercialChange
                      }
                      roleOptions={roleOptions}
                      availabilityStatusOptions={availabilityStatusOptions}
                      rateTypeOptions={rateTypeOptions}
                      engagementTypeOptions={engagementTypeOptions}
                      genderOptions={genderOptions}
                      referenceValuesLoading={referenceValuesLoading}
                      onSubmit={() => submitTab(1)}
                    />
                  )}

                  {activeTab === 2 && (
                    <Tab2Qualifications
                      travelDocuments={credentialRecords.travelDocuments}
                      setTravelDocuments={credentialRecords.setTravelDocuments}
                      marineDocuments={credentialRecords.marineDocuments}
                      setMarineDocuments={credentialRecords.setMarineDocuments}
                      educationRecords={credentialRecords.educationRecords}
                      setEducationRecords={
                        credentialRecords.setEducationRecords
                      }
                      certificationRecords={
                        credentialRecords.certificationRecords
                      }
                      setCertificationRecords={
                        credentialRecords.setCertificationRecords
                      }
                      trainingRecords={credentialRecords.trainingRecords}
                      setTrainingRecords={credentialRecords.setTrainingRecords}
                      errors={{}}
                      loading={loading}
                      addRecord={credentialRecords.addRecord}
                      removeRecord={credentialRecords.removeRecord}
                      handleRecordChange={credentialRecords.handleRecordChange}
                      handleRecordFileChange={
                        credentialRecords.handleRecordFileChange
                      }
                      removeEducationRecord={
                        credentialRecords.removeEducationRecord
                      }
                      removeCertificationRecord={
                        credentialRecords.removeCertificationRecord
                      }
                      removeTrainingRecord={
                        credentialRecords.removeTrainingRecord
                      }
                      removeCdcRecord={credentialRecords.removeCdcRecord}
                      removeCocRecord={credentialRecords.removeCocRecord}
                      onSubmit={() => submitTab(2)}
                      onBack={() => goToPrevTab(2)}
                    />
                  )}

                  {activeTab === 3 && (
                    <Tab3Experience
                      employmentRecords={experienceRecords.employmentRecords}
                      setEmploymentRecords={
                        experienceRecords.setEmploymentRecords
                      }
                      projectRecords={experienceRecords.projectRecords}
                      setProjectRecords={experienceRecords.setProjectRecords}
                      clientApprovalRecords={
                        clientApproval.clientApprovalRecords
                      }
                      setClientApprovalRecords={
                        clientApproval.setClientApprovalRecords
                      }
                      projectOptions={clientApproval.projectOptions}
                      errors={{}}
                      loading={loading}
                      addRecord={experienceRecords.addRecord}
                      removeRecord={(setter, index, creator) => {
                        if (
                          setter === clientApproval.setClientApprovalRecords
                        ) {
                          return clientApproval.removeRecord(index);
                        }
                        return experienceRecords.removeRecord(
                          setter,
                          index,
                          creator,
                        );
                      }}
                      handleRecordChange={experienceRecords.handleRecordChange}
                      handleRecordFileChange={
                        experienceRecords.handleRecordFileChange
                      }
                      removeEmploymentRecord={
                        experienceRecords.removeEmploymentRecord
                      }
                      removeProjectRecord={
                        experienceRecords.removeProjectRecord
                      }
                      onSubmitEmployment={submitEmploymentOnly}
                      onSubmitProject={submitProjectsOnly}
                      onSubmit={() => submitTab(3)}
                      onBack={() => goToPrevTab(3)}
                    />
                  )}

                  {activeTab === 4 && (
                    <Tab4Competencies
                      standardRecords={capabilityRecords.standardRecords}
                      setStandardRecords={capabilityRecords.setStandardRecords}
                      equipmentRecords={capabilityRecords.equipmentRecords}
                      setEquipmentRecords={
                        capabilityRecords.setEquipmentRecords
                      }
                      softwareRecords={capabilityRecords.softwareRecords}
                      setSoftwareRecords={capabilityRecords.setSoftwareRecords}
                      languageRecords={capabilityRecords.languageRecords}
                      setLanguageRecords={capabilityRecords.setLanguageRecords}
                      loading={loading}
                      addRecord={capabilityRecords.addRecord}
                      removeRecord={(setter, index, creator) =>
                        capabilityRecords.removeCapabilityRecord(
                          setter,
                          index,
                          creator,
                        )
                      }
                      handleRecordChange={capabilityRecords.handleRecordChange}
                      handleRecordFileChange={
                        capabilityRecords.handleRecordFileChange
                      }
                      onSubmit={() => submitTab(4)}
                      onBack={() => goToPrevTab(4)}
                    />
                  )}

                  {activeTab === 5 && (
                    <Tab5References
                      membershipRecords={referenceRecords.membershipRecords}
                      setMembershipRecords={
                        referenceRecords.setMembershipRecords
                      }
                      publicationRecords={referenceRecords.publicationRecords}
                      setPublicationRecords={
                        referenceRecords.setPublicationRecords
                      }
                      awardRecords={referenceRecords.awardRecords}
                      setAwardRecords={referenceRecords.setAwardRecords}
                      referenceRecords={referenceRecords.referenceRecords}
                      setReferenceRecords={referenceRecords.setReferenceRecords}
                      loading={loading}
                      addRecord={referenceRecords.addRecord}
                      removeRecord={(setter, index, creator) => {
                        if (setter === referenceRecords.setMembershipRecords)
                          return referenceRecords.removeMembershipRecord(index);
                        if (setter === referenceRecords.setPublicationRecords)
                          return referenceRecords.removePublicationRecord(
                            index,
                          );
                        if (setter === referenceRecords.setAwardRecords)
                          return referenceRecords.removeAwardRecord(index);
                        if (setter === referenceRecords.setReferenceRecords)
                          return referenceRecords.removeReferenceRecord(index);
                        return referenceRecords.removeRecord(
                          setter,
                          index,
                          creator,
                        );
                      }}
                      handleRecordChange={referenceRecords.handleRecordChange}
                      onSubmitMembershipsPublicationsAwards={submitMPAOnly}
                      onSubmit={() => submitTab(5)}
                      onBack={() => goToPrevTab(5)}
                    />
                  )}

                  {activeTab === 6 && (
                    <Tab6Deployment
                      emergencyContactRecords={
                        deploymentRecords.emergencyContactRecords
                      }
                      setEmergencyContactRecords={
                        deploymentRecords.setEmergencyContactRecords
                      }
                      medicalFitnessRecords={
                        deploymentRecords.medicalFitnessRecords
                      }
                      setMedicalFitnessRecords={
                        deploymentRecords.setMedicalFitnessRecords
                      }
                      ppeSizes={deploymentRecords.ppeSizes}
                      setPpeSizes={deploymentRecords.setPpeSizes}
                      declarationData={deploymentRecords.declarationData}
                      setDeclarationData={deploymentRecords.setDeclarationData}
                      loading={loading}
                      addRecord={deploymentRecords.addRecord}
                      removeRecord={(setter, index, creator) => {
                        if (
                          setter ===
                          deploymentRecords.setEmergencyContactRecords
                        )
                          return deploymentRecords.removeEmergencyContactRecord(
                            index,
                          );
                        if (
                          setter === deploymentRecords.setMedicalFitnessRecords
                        )
                          return deploymentRecords.removeMedicalFitnessRecord(
                            index,
                          );
                        return deploymentRecords.removeRecord(
                          setter,
                          index,
                          creator,
                        );
                      }}
                      handleRecordChange={deploymentRecords.handleRecordChange}
                      onSubmitDeployment={submitDeploymentOnly}
                      onSubmitDeclaration={submitDeclarationOnly}
                      onSubmit={handleSubmit}
                      setActiveTab={setActiveTab}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterStage2;
